import { useQuery } from '@tanstack/react-query';

import api from 'api';
import type {
  ModelOptionsResponse,
  VoiceOptionsResponse,
} from 'api/resources/config/types';
import useAccountRole from './useAccountRole';
import { PROJECT_RESOURCE, useProjectResource } from './useProjectResources';
import useProjectRoute from './useProjectRoute';
import useSampleProject from './useSampleProjects/useSampleProject';

export const VOICE_QUERY_KEY = 'voices';
export const MODEL_QUERY_KEY = 'models';

const useAssistantConfigOptions = (
  enabled: boolean = true,
  overrideVoiceConfigEnablement: boolean = false,
) => {
  const { accountId, projectId } = useProjectRoute();

  const { isAccountAdmin } = useAccountRole();
  const { isProjectResourceEnabled } = useProjectResource();
  const { inSampleProject } = useSampleProject();

  // admin check ensures query is run for create assistant flow at account-level
  const isVoiceConfigEnabled =
    inSampleProject ||
    isAccountAdmin ||
    overrideVoiceConfigEnablement ||
    isProjectResourceEnabled(PROJECT_RESOURCE.VOICE);
  const isModelConfigEnabled =
    inSampleProject ||
    isAccountAdmin ||
    isProjectResourceEnabled(PROJECT_RESOURCE.SETTINGS);

  const {
    data: voiceOptions,
    isLoading: isLoadingVoiceOptions,
    error: voiceOptionsError,
  } = useQuery<VoiceOptionsResponse>(
    [accountId, projectId, VOICE_QUERY_KEY],
    () => api.getVoiceOptions(accountId, projectId),
    {
      enabled: isVoiceConfigEnabled && enabled,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const { data: modelOptions, isLoading: isLoadingModelOptions } =
    useQuery<ModelOptionsResponse>(
      [accountId, projectId, MODEL_QUERY_KEY],
      () => api.getModelOptions(accountId),
      {
        enabled: isModelConfigEnabled && enabled,
        retry: false,
        refetchOnWindowFocus: false,
      },
    );

  return {
    voiceOptions: voiceOptions?.voices,
    voiceOptionsError,
    voiceTuningSettings: voiceOptions?.voice_tuning_settings ?? {},
    modelOptions: modelOptions?.models,
    isLoadingVoiceOptions,
    isLoadingModelOptions,
  };
};

export default useAssistantConfigOptions;
